import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import * as style from './style.module.css';

const ScrollableList = ({ className = '', children, shadow = 'var(--comp-scrollablelist-background-color)', arrowSpace = '0px' }) => {
  const scrollRef = useRef(null);
  const [actionsState, setActionState] = useState({ left: true, right: false, height: 0 });

  const handleClickMore = useCallback(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollLeft += scrollRef?.current?.clientWidth - 60;
    }
  }, []);

  const handleClickLess = useCallback(() => {
    if (scrollRef?.current) {
      scrollRef.current.scrollLeft -= scrollRef?.current?.clientWidth - 60;
    }
  }, []);

  useLayoutEffect(() => {
    const elem = scrollRef?.current;
    if (!elem) {
      return undefined;
    }

    // `height` is passed only when it has just been measured, and the previous
    // state object is kept when nothing moved, so scrolling through the middle
    // of the list does not re-render on every scroll event
    const update = (height = null) => {
      const current = scrollRef?.current;
      if (!current) {
        return;
      }
      const left = current.scrollLeft <= 1;
      const right = current.scrollLeft >= current.scrollWidth - current.clientWidth - 1;
      setActionState((previous) => {
        const nextHeight = height === null ? previous.height : height;
        if (previous.left === left && previous.right === right && previous.height === nextHeight) {
          return previous;
        }
        return { left, right, height: nextHeight };
      });
    };

    // getBoundingClientRect() forces a layout, so the height is measured on
    // resize only and kept out of the scroll path
    const handleScroll = () => update();
    const handleResize = () => update(Math.ceil(scrollRef?.current?.getBoundingClientRect()?.height || 0));

    handleResize();
    elem.addEventListener('scroll', handleScroll);

    // Also covers the content itself growing (deferred data arriving), which a
    // window resize listener missed
    const observer = new ResizeObserver(handleResize);
    observer.observe(elem);

    return () => {
      elem.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={`${style.list} ${className}`} style={{ '--shadow-height': `${actionsState.height}px`, '--arrow-space': arrowSpace }}>
      <div className={style.scrollable} ref={scrollRef}>
        {children}
      </div>
      <div className={`${style.shadow} ${style.less} ${actionsState.left ? style.hide : ''}`} style={{ background: `linear-gradient(-90deg, rgb(0 0 0 / 0%) 0%, ${shadow} 100%)` }}>
        <FontAwesomeIcon icon={faChevronLeft} onClick={handleClickLess} />
      </div>
      <div className={`${style.shadow} ${style.more} ${actionsState.right ? style.hide : ''}`} style={{ background: `linear-gradient(90deg, rgb(0 0 0 / 0%) 0%, ${shadow} 100%)` }}>
        <FontAwesomeIcon icon={faChevronRight} onClick={handleClickMore} />
      </div>
    </div>
  );
};

export default ScrollableList;
